import type { CountriesAlpha2, State } from '@sequencehq/api/commonEnums'
import type { Dashboardv20240730CommonModels } from '@sequencehq/api/dashboard/v20240730'
import type { ReactNode } from 'react'
import { SendConfirmationModal } from 'common/components/SteppedModal/SendConfirmationModal'
import { useAccountingLockDateValidation } from 'common/components/SteppedModal/useAccountingLockDateValidation'
import type { TaxValidationTaxCategory } from 'common/components/SteppedModal/useTaxRateValidation'
import { useTaxValidation } from 'common/components/SteppedModal/useTaxValidation'

type TaxStatus = Dashboardv20240730CommonModels.Tax.TaxStatus
type ApiLineItemGroupTaxCategory = TaxValidationTaxCategory

interface Props {
  onClose: () => void
  onConfirm: () => Promise<void>
  submitting: boolean
  customerTaxStatus?: TaxStatus
  customerCountry?: CountriesAlpha2
  customerState?: State
  invoiceAccountingDate?: string
  /** Blocker title naming the blocked action, e.g. "This invoice can't be sent". */
  blockedTitle: string
  validationSummaryText: string
  confirmationSummaryText: ReactNode
  confirmTitleText: string
  confirmActionText: string
  taxCategoriesOnInvoice: ApiLineItemGroupTaxCategory[]
  recalculateInvoice: () => void
}

export const InvoiceConfirmationModal = ({
  onClose,
  onConfirm,
  submitting,
  customerTaxStatus,
  customerCountry,
  customerState,
  invoiceAccountingDate,
  blockedTitle,
  validationSummaryText,
  confirmationSummaryText,
  taxCategoriesOnInvoice,
  confirmTitleText,
  confirmActionText,
  recalculateInvoice
}: Props) => {
  const tax = useTaxValidation({
    entityName: 'invoice',
    onClose,
    confirmTitleText,
    blockedTitle,
    customerTaxStatus,
    customerCountry,
    customerState,
    taxCategories: taxCategoriesOnInvoice,
    recalculate: recalculateInvoice
  })
  const lockDate = useAccountingLockDateValidation({
    entityName: 'invoice',
    entityDate: invoiceAccountingDate,
    blockedTitle,
    onClose
  })

  return (
    <SendConfirmationModal
      onClose={onClose}
      onConfirm={onConfirm}
      submitting={submitting}
      loading={tax.loading || lockDate.loading}
      blocker={lockDate.blocker ?? tax.blocker}
      validation={[...lockDate.validation, ...tax.validation]}
      validationSummaryText={validationSummaryText}
      confirmationSummaryText={confirmationSummaryText}
      confirmTitleText={confirmTitleText}
      confirmActionText={confirmActionText}
    />
  )
}
